

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Icon,
  IconButton,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useAuth } from "auth/Auth";
import { useSnackbar } from "notistack";
import { useMaterialUIController } from "context";
import {
  BUILTIN_MODULE_META,
  COMMON_SIDENAV_ICONS,
  CUSTOM_OBJECTS_SECTION_ID,
  getDefaultSidenavPreferences,
  mergeCustomObjectsIntoPrefs,
  reconcileSidenavSectionsForSave,
} from "sidenavCatalog";
import { useModuleEntitlements } from "../../hooks/useModuleEntitlements";
import { useFeatureLicense } from "../../hooks/useFeatureLicense";
import { builtinSidenavKeyToModuleCode } from "moduleEntitlementMap";
import useUiVersion from "hooks/useUiVersion";

function filterSectionsByEntitlements(
  sections,
  {
    enabledModuleCodes,
    hasTravelEntitlement,
    isTravelEnabled,
    isCustomObjectsEnabled,
    entitlementsReady = true,
    isV2 = false,
  }
) {
  return (sections || [])
    .filter((section) => {
      if (
        section.id === CUSTOM_OBJECTS_SECTION_ID &&
        entitlementsReady &&
        !isCustomObjectsEnabled
      ) {
        return false;
      }
      return true;
    })
    .map((section) => ({
      ...section,
      items: (section.items || []).filter((item) => {
        if (!entitlementsReady) return true;
        if (item.kind === "custom_object" || item.id?.startsWith?.("co_")) {
          return isCustomObjectsEnabled;
        }
        const moduleCode = builtinSidenavKeyToModuleCode(item.id);
        // Travel ON: hide legacy Products / Quotations / Orders from sidebar settings.
        // Not in v2, where the Sales app shows them (moduleEntitlementMap).
        if (
          !isV2 &&
          isTravelEnabled &&
          moduleCode &&
          ["PRODUCTS", "QUOTATIONS", "ORDERS"].includes(String(moduleCode).toUpperCase())
        ) {
          return false;
        }
        if (moduleCode === "TRAVEL") {
          return hasTravelEntitlement && isTravelEnabled;
        }
        if (moduleCode && enabledModuleCodes) {
          return enabledModuleCodes.has(moduleCode);
        }
        return true;
      }),
    }));
}

function newId(prefix) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function itemLabel(item) {
  if (item.kind === "custom") return item.name || "Custom Module";
  if (item.kind === "custom_object" || item.id?.startsWith?.("co_")) {
    return item.name || item.apiName || item.id?.replace(/^co_/, "") || "Custom Object";
  }
  return BUILTIN_MODULE_META[item.id]?.name || item.id;
}

function itemIcon(item) {
  if (item.kind === "custom") return item.icon || "article";
  if (item.kind === "custom_object" || item.id?.startsWith?.("co_")) {
    return item.icon || "extension";
  }
  return BUILTIN_MODULE_META[item.id]?.icon || "article";
}

function isCustomObjectNavItem(item) {
  return item?.kind === "custom_object" || item?.id?.startsWith?.("co_");
}

function normalizeSectionsForUi(sections) {
  return (sections || []).map((section) => ({
    ...section,
    items: (section.items || [])
      .filter(
        (item) =>
          section.id !== CUSTOM_OBJECTS_SECTION_ID || isCustomObjectNavItem(item)
      )
      .map((item) =>
        isCustomObjectNavItem(item)
          ? {
              ...item,
              kind: "custom_object",
              apiName: item.apiName || item.api_name || item.id?.replace(/^co_/, ""),
              icon: item.icon || "extension",
            }
          : item
      ),
  }));
}

function SidebarSettings() {
  const { axiosInstance } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const {
    enabledModuleCodes,
    hasTravelEntitlement,
    loading: entitlementsLoading,
  } = useModuleEntitlements();
  const { features, loading: featureLicensesLoading } = useFeatureLicense();
  const { isV2 } = useUiVersion();
  const [companyTravelEnabled, setCompanyTravelEnabled] = useState(false);

  const [sections, setSections] = useState([]);
  const [storedSections, setStoredSections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);

  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState("");

  const [savingItemId, setSavingItemId] = useState(null);

  const [addModuleOpen, setAddModuleOpen] = useState(false);
  const [newModuleName, setNewModuleName] = useState("");
  const [newModuleIcon, setNewModuleIcon] = useState("article");
  const [newModuleSectionId, setNewModuleSectionId] = useState("");
  const [newModuleBody, setNewModuleBody] = useState("");

  const load = useCallback(async ({ force = false } = {}) => {
    // Do not clobber in-progress drag/edit state when entitlements refresh.
    if (dirtyRef.current && !force) return;

    setIsLoading(true);
    setError(null);
    try {
      const prefsRes = await axiosInstance.get("/users/me/preferences");
      const prefs = prefsRes.data?.sidenav_preferences;

      let customObjects = null;
      if (features.custom_objects) {
        const objectsRes = await axiosInstance.get("/custom-objects").catch(() => null);
        customObjects = objectsRes
          ? Array.isArray(objectsRes.data)
            ? objectsRes.data
            : objectsRes.data?.items || objectsRes.data?.objects || []
          : null;
      }

      let travelEnabled = companyTravelEnabled;
      try {
        const companyRes = await axiosInstance.get("/company/info");
        travelEnabled = companyRes.data?.is_travel_enabled === true;
        setCompanyTravelEnabled(travelEnabled);
      } catch {
        setCompanyTravelEnabled(false);
        travelEnabled = false;
      }

      const { prefs: merged, added } = mergeCustomObjectsIntoPrefs(
        prefs?.sections?.length ? prefs : getDefaultSidenavPreferences(),
        customObjects
      );
      const normalized = normalizeSectionsForUi(merged.sections);
      setStoredSections(normalized);
      const filtered = filterSectionsByEntitlements(normalized, {
        enabledModuleCodes,
        hasTravelEntitlement,
        isTravelEnabled: hasTravelEntitlement && travelEnabled,
        isCustomObjectsEnabled: Boolean(features.custom_objects),
        entitlementsReady: !entitlementsLoading && !featureLicensesLoading,
        isV2,
      });
      setSections(filtered);
      dirtyRef.current = Boolean(added);
      setDirty(Boolean(added));
    } catch (err) {
      console.error(err);
      setError("Failed to load sidebar preferences.");
      const fallback = getDefaultSidenavPreferences().sections;
      setStoredSections(fallback);
      setSections(fallback);
    } finally {
      setIsLoading(false);
    }
  }, [
    axiosInstance,
    companyTravelEnabled,
    enabledModuleCodes,
    entitlementsLoading,
    featureLicensesLoading,
    features.custom_objects,
    hasTravelEntitlement,
    isV2,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const handleTravelUpdate = (event) => {
      const enabled = event?.detail?.enabled === true;
      setCompanyTravelEnabled(enabled);
      // Force reload so Travel appears in the editor once company flag is on.
      dirtyRef.current = false;
      load({ force: true });
    };
    window.addEventListener("travelFeatureUpdated", handleTravelUpdate);
    return () => window.removeEventListener("travelFeatureUpdated", handleTravelUpdate);
  }, [load]);

  const markDirty = (nextSections) => {
    setSections(nextSections);
    dirtyRef.current = true;
    setDirty(true);
  };

  const persistSections = useCallback(
    async (nextSections, { silent = false } = {}) => {
      setIsSaving(true);
      setError(null);
      try {

        const finalSections = reconcileSidenavSectionsForSave(
          nextSections,
          storedSections
        );
        const savePayload = { sections: finalSections };
        const res = await axiosInstance.patch("/users/me/preferences", {
          sidenav_preferences: savePayload,
        });
        const saved = res.data?.sidenav_preferences || savePayload;
        const normalized = normalizeSectionsForUi(saved.sections || savePayload.sections);
        setStoredSections(normalized);
        const filtered = filterSectionsByEntitlements(normalized, {
          enabledModuleCodes,
          hasTravelEntitlement,
          isTravelEnabled: hasTravelEntitlement && companyTravelEnabled,
          isCustomObjectsEnabled: Boolean(features.custom_objects),
          entitlementsReady: !entitlementsLoading && !featureLicensesLoading,
        });
        setSections(filtered);
        dirtyRef.current = false;
        setDirty(false);
        window.dispatchEvent(
          new CustomEvent("sidenavPreferencesUpdated", { detail: saved })
        );
        if (!silent) {
          enqueueSnackbar("Sidebar preferences saved", { variant: "success" });
        }
      } catch (err) {
        console.error(err);
        setError("Failed to save sidebar preferences.");
        enqueueSnackbar("Failed to save sidebar changes", { variant: "error" });
      } finally {
        setIsSaving(false);
      }
    },
    [
      axiosInstance,
      companyTravelEnabled,
      enabledModuleCodes,
      enqueueSnackbar,
      entitlementsLoading,
      featureLicensesLoading,
      features.custom_objects,
      hasTravelEntitlement,
      storedSections,
    ]
  );

  const canDropItemInSection = (item, destSection) => {
    if (!destSection) return false;
    // Only custom-object modules may enter the dedicated Custom Objects section.
    if (destSection.id === CUSTOM_OBJECTS_SECTION_ID) {
      return isCustomObjectNavItem(item);
    }
    return true;
  };

  const onDragEnd = (result) => {
    const { destination, source, type } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    if (type === "SECTION") {
      const next = Array.from(sections);
      const [removed] = next.splice(source.index, 1);
      next.splice(destination.index, 0, removed);
      markDirty(next);
      return;
    }

    // Item drag — droppableId is section id
    const sourceSectionIndex = sections.findIndex((s) => s.id === source.droppableId);
    const destSectionIndex = sections.findIndex((s) => s.id === destination.droppableId);
    if (sourceSectionIndex < 0 || destSectionIndex < 0) return;

    const next = sections.map((s) => ({ ...s, items: [...(s.items || [])] }));
    const [moved] = next[sourceSectionIndex].items.splice(source.index, 1);
    if (!moved) return;

    const destSection = next[destSectionIndex];
    if (!canDropItemInSection(moved, destSection)) {
      next[sourceSectionIndex].items.splice(source.index, 0, moved);
      return;
    }

    next[destSectionIndex].items.splice(destination.index, 0, moved);
    markDirty(next);
  };

  const toggleSectionHidden = (sectionId) => {
    markDirty(
      sections.map((s) =>
        s.id === sectionId ? { ...s, hidden: !s.hidden, visibilitySetByUser: true } : s
      )
    );
  };

  const toggleItemHidden = async (sectionId, itemId) => {
    const next = sections.map((s) => {
      if (s.id !== sectionId) return s;
      return {
        ...s,
        items: (s.items || []).map((item) =>
          item.id === itemId
            ? { ...item, hidden: !item.hidden, visibilitySetByUser: true }
            : item
        ),
      };
    });
    markDirty(next);

    const section = next.find((s) => s.id === sectionId);
    const toggled = section?.items?.find((item) => item.id === itemId);
    if (isCustomObjectNavItem(toggled)) {
      // Persist custom object visibility immediately so refresh doesn't revert,
      // and show a loader on this item until the sidenav reflects the change.
      setSavingItemId(itemId);
      try {
        await persistSections(next, { silent: true });
      } finally {
        setSavingItemId(null);
      }
    }
  };

  const deleteCustomSection = (sectionId) => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section || section.kind !== "custom") return;
    if (!window.confirm(`Delete section "${section.title}" and its custom modules?`)) return;
    markDirty(sections.filter((s) => s.id !== sectionId));
  };

  const deleteCustomItem = (sectionId, itemId) => {
    const section = sections.find((s) => s.id === sectionId);
    const item = section?.items?.find((i) => i.id === itemId);
    if (!item || item.kind !== "custom") return;
    if (!window.confirm(`Delete custom module "${item.name}"?`)) return;
    markDirty(
      sections.map((s) => {
        if (s.id !== sectionId) return s;
        return { ...s, items: (s.items || []).filter((i) => i.id !== itemId) };
      })
    );
  };

  const handleAddSection = () => {
    const title = newSectionTitle.trim();
    if (!title) return;
    markDirty([
      ...sections,
      {
        id: newId("sec"),
        title,
        kind: "custom",
        hidden: false,
        items: [],
      },
    ]);
    setNewSectionTitle("");
    setAddSectionOpen(false);
  };

  const handleAddModule = () => {
    const name = newModuleName.trim();
    if (!name || !newModuleSectionId) return;
    const module = {
      id: newId("cmod"),
      kind: "custom",
      hidden: false,
      name,
      icon: newModuleIcon || "article",
      body: newModuleBody || "",
    };
    markDirty(
      sections.map((s) =>
        s.id === newModuleSectionId
          ? { ...s, items: [...(s.items || []), module] }
          : s
      )
    );
    setNewModuleName("");
    setNewModuleBody("");
    setNewModuleIcon("article");
    setNewModuleSectionId("");
    setAddModuleOpen(false);
  };

  const handleSave = async () => {
    await persistSections(sections, { silent: false });
  };

  const handleReset = async () => {
    if (
      !window.confirm(
        "Reset sidebar to the default layout? This will remove custom sections and modules. Custom objects stay on the sidenav and can be hidden individually."
      )
    ) {
      return;
    }
    try {
      const objectsRes = await axiosInstance.get("/custom-objects").catch(() => ({ data: [] }));
      const customObjects = Array.isArray(objectsRes.data)
        ? objectsRes.data
        : objectsRes.data?.items || objectsRes.data?.objects || [];
      const { prefs: merged } = mergeCustomObjectsIntoPrefs(
        getDefaultSidenavPreferences(),
        customObjects
      );
      markDirty(normalizeSectionsForUi(merged.sections));
    } catch (err) {
      console.error(err);
      markDirty(getDefaultSidenavPreferences().sections);
    }
  };

  if (isLoading) {
    return (
      <Box p={2}>
        <MDTypography variant="body2">Loading sidebar preferences…</MDTypography>
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <MDTypography className="pearl-dialog-dup-title" variant="h6" mb={1} fontWeight="medium" color="info">
        Sidebar Navigation
      </MDTypography>
      <MDTypography variant="body2" mb={2} color="text">
        Choose which modules you see, reorder them, and add your own sections and pages.
        Objects you create appear under the <strong>Custom Objects</strong> section (hidden by default).
        You can unhide them or drag them into another section.
        Role and travel feature gates still apply and cannot be overridden.
      </MDTypography>

      <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
        <MDButton
          variant="gradient"
          color="info"
          size="small"
          onClick={handleSave}
          disabled={isSaving || !dirty}
        >
          {isSaving ? "Saving…" : "Save"}
        </MDButton>
        <MDButton
          variant="outlined"
          color="secondary"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setAddSectionOpen(true)}
        >
          Add section
        </MDButton>
        <MDButton
          variant="outlined"
          color="secondary"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => {
            setNewModuleSectionId(sections[0]?.id || "");
            setAddModuleOpen(true);
          }}
        >
          Add module
        </MDButton>
        <MDButton variant="text" color="warning" size="small" onClick={handleReset}>
          Reset to default
        </MDButton>
      </Box>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="sections" type="SECTION">
          {(sectionDropProvided) => (
            <Box ref={sectionDropProvided.innerRef} {...sectionDropProvided.droppableProps}>
              {sections.map((section, sectionIndex) => (
                <Draggable key={section.id} draggableId={`section-${section.id}`} index={sectionIndex}>
                  {(sectionDragProvided) => (
                    <Box
                      ref={sectionDragProvided.innerRef}
                      {...sectionDragProvided.draggableProps}
                      sx={{
                        mb: 2,
                        p: 1.5,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        opacity: section.hidden ? 0.55 : 1,
                        bgcolor: darkMode ? "rgba(255,255,255,0.03)" : "background.paper",
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Box {...sectionDragProvided.dragHandleProps} sx={{ display: "flex", cursor: "grab" }}>
                          <DragIndicatorIcon fontSize="small" color="disabled" />
                        </Box>
                        <MDTypography variant="button" fontWeight="bold" sx={{ flex: 1 }}>
                          {section.title}
                          {section.kind === "custom" && (
                            <MDTypography component="span" variant="caption" color="text" ml={1}>
                              (custom)
                            </MDTypography>
                          )}
                          {section.id === CUSTOM_OBJECTS_SECTION_ID && (
                            <MDTypography component="span" variant="caption" color="text" ml={1}>
                              (your objects)
                            </MDTypography>
                          )}
                        </MDTypography>
                        <Tooltip title={section.hidden ? "Show section" : "Hide section"}>
                          <IconButton size="small" onClick={() => toggleSectionHidden(section.id)}>
                            {section.hidden ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        {section.kind === "custom" && (
                          <Tooltip title="Delete section">
                            <IconButton size="small" color="error" onClick={() => deleteCustomSection(section.id)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>

                      <Droppable droppableId={section.id} type="ITEM">
                        {(itemDropProvided) => (
                          <List
                            dense
                            ref={itemDropProvided.innerRef}
                            {...itemDropProvided.droppableProps}
                            sx={{ minHeight: 40 }}
                          >
                            {(section.items || []).map((item, itemIndex) => (
                              <Draggable
                                key={item.id}
                                draggableId={`item-${item.id}`}
                                index={itemIndex}
                              >
                                {(itemDragProvided) => (
                                  <ListItem
                                    ref={itemDragProvided.innerRef}
                                    {...itemDragProvided.draggableProps}
                                    sx={{
                                      borderRadius: 1,
                                      mb: 0.5,
                                      bgcolor: darkMode
                                        ? "rgba(255,255,255,0.04)"
                                        : "rgba(0,0,0,0.02)",
                                      opacity: item.hidden ? 0.5 : 1,
                                    }}
                                    secondaryAction={
                                      <Box>
                                        <Tooltip title={savingItemId === item.id ? "Updating…" : item.hidden ? "Show" : "Hide"}>
                                          <IconButton
                                            edge="end"
                                            size="small"
                                            disabled={savingItemId === item.id}
                                            onClick={() => toggleItemHidden(section.id, item.id)}
                                          >
                                            {savingItemId === item.id ? (
                                              <CircularProgress size={16} color="info" />
                                            ) : item.hidden ? (
                                              <VisibilityOffIcon fontSize="small" />
                                            ) : (
                                              <VisibilityIcon fontSize="small" />
                                            )}
                                          </IconButton>
                                        </Tooltip>
                                        {item.kind === "custom" && (
                                          <Tooltip title="Delete module">
                                            <IconButton
                                              edge="end"
                                              size="small"
                                              color="error"
                                              onClick={() => deleteCustomItem(section.id, item.id)}
                                            >
                                              <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </Box>
                                    }
                                  >
                                    <Box
                                      {...itemDragProvided.dragHandleProps}
                                      sx={{ display: "flex", mr: 1, cursor: "grab" }}
                                    >
                                      <DragIndicatorIcon fontSize="small" color="disabled" />
                                    </Box>
                                    <Icon fontSize="small" sx={{ mr: 1 }}>
                                      {itemIcon(item)}
                                    </Icon>
                                    <MDTypography variant="button" fontWeight="regular">
                                      {itemLabel(item)}
                                      {item.kind === "custom" && (
                                        <MDTypography component="span" variant="caption" color="text" ml={0.5}>
                                          (custom)
                                        </MDTypography>
                                      )}
                                      {isCustomObjectNavItem(item) && (
                                        <MDTypography component="span" variant="caption" color="text" ml={0.5}>
                                          (custom object)
                                        </MDTypography>
                                      )}
                                    </MDTypography>
                                  </ListItem>
                                )}
                              </Draggable>
                            ))}
                            {itemDropProvided.placeholder}
                            {(section.items || []).length === 0 && (
                              <MDTypography variant="caption" color="text" px={1}>
                                {section.id === CUSTOM_OBJECTS_SECTION_ID
                                  ? "New custom objects appear here. You can also drag them to another section."
                                  : "Drop modules here or add a custom module."}
                              </MDTypography>
                            )}
                          </List>
                        )}
                      </Droppable>
                    </Box>
                  )}
                </Draggable>
              ))}
              {sectionDropProvided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      <Dialog open={addSectionOpen} onClose={() => setAddSectionOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Add custom section</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Section title"
            value={newSectionTitle}
            onChange={(e) => setNewSectionTitle(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddSection()}
          />
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={() => setAddSectionOpen(false)}>
            Cancel
          </MDButton>
          <MDButton color="info" variant="gradient" onClick={handleAddSection} disabled={!newSectionTitle.trim()}>
            Add
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={addModuleOpen} onClose={() => setAddModuleOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add custom module</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Module name"
            value={newModuleName}
            onChange={(e) => setNewModuleName(e.target.value)}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel id="section-select-label">Section</InputLabel>
            <Select
              labelId="section-select-label"
              label="Section"
              value={newModuleSectionId}
              onChange={(e) => setNewModuleSectionId(e.target.value)}
            >
              {sections.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel id="icon-select-label">Icon</InputLabel>
            <Select
              labelId="icon-select-label"
              label="Icon"
              value={newModuleIcon}
              onChange={(e) => setNewModuleIcon(e.target.value)}
              renderValue={(val) => (
                <Box display="flex" alignItems="center" gap={1}>
                  <Icon fontSize="small">{val}</Icon>
                  {val}
                </Box>
              )}
            >
              {COMMON_SIDENAV_ICONS.map((iconName) => (
                <MenuItem key={iconName} value={iconName}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Icon fontSize="small">{iconName}</Icon>
                    {iconName}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="dense"
            label="Initial content (optional)"
            multiline
            minRows={3}
            value={newModuleBody}
            onChange={(e) => setNewModuleBody(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={() => setAddModuleOpen(false)}>
            Cancel
          </MDButton>
          <MDButton
            color="info"
            variant="gradient"
            onClick={handleAddModule}
            disabled={!newModuleName.trim() || !newModuleSectionId}
          >
            Add
          </MDButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SidebarSettings;
